import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { api, errMsg, fmtMoney, fmtDate } from "../../lib/api";
import { serviceName } from "../../lib/constants";
import { StatusBadge } from "../../components/shared/StatusBadge";
import { Button } from "../../components/ui/button";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";

export default function PortalQuotes() {
  const [quotes, setQuotes] = useState([]);
  const [acting, setActing] = useState(null);

  const load = () => api.get("/portal/quotes").then((r) => setQuotes(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const act = async (qid, action) => {
    setActing(qid);
    try {
      const { data } = await api.post(`/portal/quotes/${qid}/${action}`);
      if (action === "accept") toast.success(`Booked! Mission ${data.mission_number} confirmed.`);
      else toast.success("Quote declined");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
    setActing(null);
  };

  return (
    <div data-testid="portal-quotes-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading font-bold text-2xl">My Quote Requests</h1>
        <Link to="/quote"><Button data-testid="portal-quotes-new-button" size="sm" className="rounded-full btn-glow bg-[var(--cc-accent)] text-black hover:bg-[var(--cc-accent)]/90 font-semibold">New Request</Button></Link>
      </div>
      <div className="space-y-4">
        {quotes.length === 0 && (
          <div className="rounded-xl border border-dashed p-10 text-center">
            <p className="text-muted-foreground text-sm">No quote requests yet. <Link to="/quote" className="text-[var(--cc-accent)] font-semibold">Request your first quote</Link> or <Link to="/book" className="text-[var(--cc-accent)] font-semibold">book instantly</Link>.</p>
          </div>
        )}
        {quotes.map((q) => (
          <div key={q.id} data-testid={`portal-quote-${q.id}`} className={`rounded-xl border bg-card p-6 card-lift ${q.status === "quoted" ? "border-[var(--cc-accent)]/50" : ""}`}>
            <div className="flex flex-wrap gap-4 items-center justify-between">
              <div>
                <p className="font-heading font-semibold">{serviceName(q.service_type)}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  Submitted {fmtDate(q.created_at)}{q.preferred_date ? ` · Preferred date ${fmtDate(q.preferred_date)}` : ""}
                </p>
                {q.pickup_address && <p className="text-xs text-muted-foreground mt-0.5">{q.pickup_address}{q.dropoff_address ? ` → ${q.dropoff_address}` : ""}</p>}
              </div>
              <StatusBadge status={q.status} />
            </div>
            {q.status === "quoted" && (
              <div className="mt-5 pt-5 border-t" data-testid={`portal-quote-offer-${q.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-4">
                  <div>
                    <p className="overline-cc">Your Flat Rate</p>
                    <p className="font-heading font-extrabold text-3xl mt-1">{fmtMoney(q.quoted_price)}</p>
                    {q.quote_message && <p className="text-sm text-muted-foreground mt-2 max-w-md leading-relaxed">{q.quote_message}</p>}
                  </div>
                  <div className="flex gap-3">
                    <Button data-testid={`portal-quote-accept-${q.id}`} disabled={acting === q.id} onClick={() => act(q.id, "accept")}
                      className="rounded-full btn-glow bg-[var(--cc-accent)] text-black hover:bg-[var(--cc-accent)]/90 font-semibold px-6">
                      {acting === q.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle2 className="w-4 h-4 mr-2" /> Accept & Book</>}
                    </Button>
                    <Button data-testid={`portal-quote-decline-${q.id}`} variant="outline" disabled={acting === q.id} onClick={() => act(q.id, "decline")} className="rounded-full">
                      Decline
                    </Button>
                  </div>
                </div>
              </div>
            )}
            {q.status === "booked" && (
              <p className="mt-4 pt-4 border-t text-sm text-muted-foreground">
                Booked at {fmtMoney(q.quoted_price)} — track it in <Link to="/portal/jobs" className="text-[var(--cc-accent)] font-semibold">My Jobs</Link>.
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
